// 連続クリック時の処理を制御するための時間
import apiService from '@/member/services/apiService'

const processingInterval = 500

const getDefaultState = () => {
  return {
    // selectedAreasの値
    // { management_plan_id, forest_group, small_forest_group, branch_group, compensate_group }
    selectedAreas: [],
    // 選択エリアの処理時刻
    // memo: 1度のクリックイベントで複数回処理されないようにするために使用する
    selectedAreaProcessedAt: {},
    simulationSetting: null,
    justSelectedAreaForestHierarchy: {},
    // FCS2024
    selectedMode: '',
    selectedTreeSpecies: [],
    selectedTreeAges: [],
    selectedOperationPlanned: [],
  }
}

const state = getDefaultState()

const mutations = {
  RESET_SELECTED_AREAS(state) {
    state.selectedAreas = []
  },
  SET_JUST_SELECTED_AREA_FOREST_HIERARCHY(state, payload) {
    state.justSelectedAreaForestHierarchy = {
      operation: payload.operation,
      forest_group: parseInt(payload.forest_group),
      small_forest_group: payload.small_forest_group,
      branch_group: parseInt(payload.branch_group),
      compensate_group: parseInt(payload.compensate_group),
    }
  },
  /**
   * 選択エリアを設定する
   * @param state
   * @param payload {Object} 選択エリア情報
   *  'operation' キーが存在する場合は、選択エリアの追加・削除を行う
   *  'operation' = 'add' : 選択エリアを追加する
   *  'operation' = 'remove' : 選択エリアを削除する
   * @constructor
   */
  SET_SELECTED_AREA(state, payload) {
    // 前回の処理から一定時間経過していない場合は処理しない
    const now = new Date()
    const nowTimestamp = now.getTime()
    const processedAtKey = `${payload.forest_group}_${payload.small_forest_group}_${payload.branch_group}_${payload.compensate_group}`
    const lastProcessedAt = state.selectedAreaProcessedAt[processedAtKey]

    if (lastProcessedAt && nowTimestamp - lastProcessedAt < processingInterval) {
      return
    }
    state.selectedAreaProcessedAt[processedAtKey] = nowTimestamp

    // 選択済判定
    let selectedAreasIndex = null
    const isAlreadySelected = state.selectedAreas.some((selectedArea, i) => {
      const result =
        parseInt(selectedArea.forest_group) === parseInt(payload.forest_group) &&
        selectedArea.small_forest_group === payload.small_forest_group &&
        parseInt(selectedArea.branch_group) === parseInt(payload.branch_group) &&
        parseInt(selectedArea.compensate_group) === parseInt(payload.compensate_group)

      if (result) {
        selectedAreasIndex = i
      }

      return result
    })

    // 外部からの操作を受け付ける場合
    if ('operation' in payload) {
      if (payload.operation === 'add' && !isAlreadySelected) {
        delete payload.operation
        state.selectedAreas.push(payload)
      } else if (payload.operation === 'remove' && isAlreadySelected) {
        state.selectedAreas.splice(selectedAreasIndex, 1)
      }
      return
    }

    if (!isAlreadySelected) {
      // 選択済の場合 エリアに追加する
      state.selectedAreas.push(payload)
    } else {
      // 選択済の場合 エリアを解除する
      state.selectedAreas.splice(selectedAreasIndex, 1)
    }
  },
  SET_SELECTED_AREAS_BY_SETTING(state, payload) {
    state.selectedAreas = []

    // 林小班で重複しないようにする
    payload.forEach(area => {
      const selectedArea = state.selectedAreas.find(selectedArea => {
        return (
          selectedArea['forest_group'] === area['forest_group'] &&
          selectedArea['small_forest_group'] === area['small_forest_group'] &&
          selectedArea['branch_group'] === area['branch_group'] &&
          selectedArea['compensate_group'] === area['compensate_group']
        )
      })

      if (!selectedArea) {
        state.selectedAreas.push(area)
      }
    })
  },
  SET_SIMULATION_SETTING(state, payload) {
    state.simulationSetting = payload
  },
  SET_SELECTED_MODE(state, payload) {
    state.selectedMode = payload
  },
  SET_TREE_SPECIES(state, payload) {
    state.selectedTreeSpecies = payload
  },
  SET_SELECTED_TREE_AGES(state, payload) {
    state.selectedTreeAges = payload
  },
  SET_SELECTED_OPERATION_PLANNED(state, payload) {
    state.selectedOperationPlanned = payload
  },
}

const actions = {
  async resetSelectedAreas({ commit }, payload) {
    commit('RESET_SELECTED_AREAS', payload)
  },
  // 直前に選ばれたエリアの森林情報をセットする
  async setJustSelectedAreaForestHierarchy({ commit }, payload) {
    commit('SET_JUST_SELECTED_AREA_FOREST_HIERARCHY', payload)
  },
  async setSelectedArea({ commit }, payload) {
    commit('SET_SELECTED_AREA', payload)
  },
  async setSelectedAreasBySetting({ commit }, payload) {
    commit('SET_SELECTED_AREAS_BY_SETTING', payload)
  },
  async setSimulationSetting({ commit }, payload) {
    commit('SET_SIMULATION_SETTING', payload)
  },
  async getSpatialFileSettings({ commit }, payload) {
    const apiResult = await apiService.get(route('member.fcs.spatial-file.get-spatial-file-settings'), payload)
    if (apiResult && apiResult.data) {
      return apiResult.data
    }
  },
  async getTileSet({ commit }, payload) {
    const apiResult = await apiService.get(route('member.fcs.mapbox-tileset.get-mapbox-tileset'), payload)
    if (apiResult && apiResult.data) {
      return apiResult.data
    }
  },
  async setSelectedMode({ commit }, payload) {
    commit('SET_SELECTED_MODE', payload)
  },
  async setTreeSpecies({ commit }, payload) {
    commit('SET_TREE_SPECIES', payload)
  },
  async setSelectedAges({ commit }, payload) {
    commit('SET_SELECTED_TREE_AGES', payload)
  },
  async setOperationPlanned({ commit }, payload) {
    commit('SET_SELECTED_OPERATION_PLANNED', payload)
  },
}

const getters = {
  justSelectedAreaForestHierarchy(state) {
    return state.justSelectedAreaForestHierarchy ?? null
  },
  selectedAreas(state) {
    return state.selectedAreas ?? []
  },
  simulationSetting(state) {
    return state.simulationSetting ?? null
  },
  selectedMode(state) {
    return state.selectedMode ?? null
  },
  selectedTreeSpecies(state) {
    return state.selectedTreeSpecies ?? null
  },
  selectedTreeAges(state) {
    return state.selectedTreeAges ?? null
  },
  selectedOperationPlanned(state) {
    return state.selectedOperationPlanned ?? null
  },
}

export default {
  state,
  getters,
  mutations,
  actions,
}
